import React, { useEffect, useRef } from 'react';
import firebase from '../../config/firebase.config';
import { addNotification, notificationStates } from '../Notification';
import { getTeamMemberDetails } from '../../services/teamMemberDetails';
import { moduleIds, useFeatures } from '@/hooks/useFeatures';

const database = firebase.firestore();

/**
 * AlertListener Component
 * Listens to Firestore alerts subcollection and shows toast notifications
 * Alerts are stored in membersDatabase/{userId}/alerts subcollection
 */
const AlertListener = ({ userId }) => {
  const unsubscribeRef = useRef(null);
  const processedAlertsRef = useRef(new Set()); // Track processed alerts to avoid duplicates
  const isInitialLoadRef = useRef(true); // Use ref instead of state to avoid re-renders
  const { hasAccess } = useFeatures();
  useEffect(() => {
    if (!userId) {
      return;
    }
    const notificationsQuery = database
      .collection('membersDatabase')
      .doc(userId)
      .collection('notifications')
      .where('isRead', '==', false)
      .where('notificationType', 'in', [
        'TIMELINE_EXPORT',
        'USER_ATTENDANCE_EXPORT',
      ]);
    const unsubscribe = notificationsQuery.onSnapshot((snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          if (change.doc.data().exportStatus === 'PENDING') {
            addNotification({
              message: 'Preparing your export…',
              level: notificationStates.info,
              autoDismiss: 4,
            });
          }
        }
        if (change.type === 'modified') {
          if (change.doc.data().exportStatus === 'COMPLETED') {
            addNotification({
              message:
                'Your export is ready, Download link sent to your email.',
              level: notificationStates.info,
              autoDismiss: 4,
            });
          } else if (change.doc.data().exportStatus === 'FAILED') {
            addNotification({
              message: 'Your export failed, please try again.',
              level: notificationStates.error,
              autoDismiss: 4,
            });
          }
        }
      });
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [userId]);

  useEffect(() => {
    if (!hasAccess.module(moduleIds.PRODUCTIVITY_ALERT)?.enabled) {
      return;
    }
    // Skip if userId is not available
    if (!userId) {
      console.log(
        '🔔 AlertListener: userId not available, skipping listener setup'
      );
      return;
    }

    console.log(
      '🔔 AlertListener: Setting up Firestore listener for userId:',
      userId
    );

    // Reset initial load flag when userId changes
    isInitialLoadRef.current = true;
    processedAlertsRef.current.clear();

    // Set up Firestore listener
    // Query alerts from user's subcollection: membersDatabase/{userId}/alerts
    const alertsQuery = database
      .collection('membersDatabase')
      .doc(userId)
      .collection('alerts')
      .where('isRead', '==', false)
      .orderBy('createdAt', 'desc')
      .limit(50);

    unsubscribeRef.current = alertsQuery.onSnapshot(
      (snapshot) => {
        console.log('🔔 AlertListener: Snapshot received', {
          docChanges: snapshot.docChanges().length,
          totalDocs: snapshot.size,
          isInitialLoad: isInitialLoadRef.current,
        });

        // Process each document change
        snapshot.docChanges().forEach((change) => {
          const alertData = {
            id: change.doc.id,
            ...change.doc.data(),
          };

          // Handle different change types
          if (change.type === 'added') {
            // Check if this is a new alert or an existing one from initial load
            if (isInitialLoadRef.current) {
              // On initial load, mark all alerts as processed but don't show notifications
              processedAlertsRef.current.add(alertData.id);
              console.log(
                '🔔 AlertListener: Initial load - marking alert as processed:',
                alertData.id
              );
            } else {
              // This is a truly new alert (added after initial load)
              if (!processedAlertsRef.current.has(alertData.id)) {
                processedAlertsRef.current.add(alertData.id);
                // showAlertNotification is async, but we don't need to await it
                showAlertNotification(alertData).catch((err) => {
                  console.error(
                    '🔔 AlertListener: Error showing notification:',
                    err
                  );
                });
              }
            }
          } else if (change.type === 'modified') {
            console.log('🔔 AlertListener: Alert modified:', alertData.id);
            // Handle modified alerts if needed (e.g., if isRead changed to true, remove from processed)
            if (alertData.isRead === true) {
              processedAlertsRef.current.delete(alertData.id);
            }
          } else if (change.type === 'removed') {
            console.log('🔔 AlertListener: Alert removed:', alertData.id);
            processedAlertsRef.current.delete(alertData.id);
          }
        });

        // Mark initial load as complete after first snapshot
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
          console.log(
            '🔔 AlertListener: Initial load complete. Now listening for new alerts.'
          );
        }
      },
      (error) => {
        console.error('🔔 AlertListener: Error listening to alerts:', error);

        // Check if it's an index error (common when testing)
        if (error.code === 'failed-precondition') {
          console.error(
            '🔔 AlertListener: Firestore index required! Please create a composite index for:'
          );
          console.error('   Collection: membersDatabase/{userId}/alerts');
          console.error(
            '   Fields: isRead (Ascending), createdAt (Descending)'
          );
          addNotification({
            message:
              'Firestore index required for alerts. Check console for details.',
            level: notificationStates.warning,
            autoDismiss: 10,
          });
        } else {
          addNotification({
            message: `Error loading alerts: ${error.message}. Check console for details.`,
            level: notificationStates.error,
            autoDismiss: 10,
          });
        }
      }
    );

    // Cleanup function
    return () => {
      console.log('🔔 AlertListener: Cleaning up listener');
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      isInitialLoadRef.current = true;
      processedAlertsRef.current.clear();
    };
  }, [userId]);

  /**
   * Show alert as toast notification
   * Format: "Low Productivity Alert : {Employee Name} from {Team Name}"
   */
  const showAlertNotification = async (alertData) => {
    console.log('🔔 AlertListener: Showing alert notification:', alertData);

    // Determine notification level based on severity
    let notificationLevel = notificationStates.info;
    if (alertData.severity === 'high') {
      notificationLevel = notificationStates.error;
    } else if (alertData.severity === 'medium') {
      notificationLevel = notificationStates.warning;
    } else if (alertData.severity === 'low') {
      notificationLevel = notificationStates.info;
    }

    // Fetch team name and employee name for notification
    let employeeName = 'Unknown Employee';
    let teamName = 'Unknown Team';

    try {
      // Fetch team name
      if (alertData.teamId) {
        const teamDoc = await database
          .collection('teamDatabase')
          .doc(alertData.teamId)
          .get();

        if (teamDoc.exists) {
          const teamData = teamDoc.data();
          teamName = teamData.teamName || teamData.Team || 'Unknown Team';
        }
      }

      // Fetch employee name using getTeamMemberDetails (supports sharded memberDetails)
      if (alertData.teamId && alertData.userId) {
        const memberDetails = await getTeamMemberDetails(
          database,
          alertData.teamId
        );
        const memberInfo = memberDetails[alertData.userId];

        if (memberInfo) {
          employeeName =
            memberInfo.userName ||
            memberInfo.userEmailid ||
            alertData.metadata?.userEmailId ||
            'Unknown Employee';
        } else if (alertData.metadata?.userEmailId) {
          employeeName = alertData.metadata.userEmailId;
        }
      }
    } catch (err) {
      console.error(
        '🔔 AlertListener: Error fetching team/employee names:',
        err
      );
      // Fallback to metadata if available
      if (alertData.metadata?.userEmailId) {
        employeeName = alertData.metadata.userEmailId;
      }
    }

    // Format message: "Low Productivity Alert : {Employee Name} from {Team Name}"
    const message = `Low Productivity Alert : ${employeeName} from ${teamName}`;

    // Show toast notification
    addNotification({
      message: message,
      level: notificationLevel,
      autoDismiss: alertData.severity === 'high' ? 10 : 5, // High severity alerts stay longer
    });
  };

  // This component doesn't render anything
  return null;
};

export default AlertListener;
